import { Component, Prop, Watch } from 'vue-property-decorator'
import Vue, { CreateElement } from '@src/vue'
import { BOTTOM_SCROLL_THRESHOLD } from '@src/util/constants'
import { Loader } from '@src/components/loading-screen/loader/loader'

import './infinite-scroll-list.sass'

/*
InfiniteScrollList will automatically load the next page, display loading icon, end page message and error message
if provided. In case of pages containing filters, you can provide them to this component, it will reset the current
page and start all over when filters change.

Individual components are used to avoid unnecessary rendering at every small change (like loader icon).
These individual components listen to props in store meaning they are reactive.

Tip: to avoid unnecessary api calls in case of filtering (eg: filter by name), delay the filter change by 400ms.
*/

@Component({
  computed: {
    loading () {
      return this.$store.state.infiniteScrollState.loading
    }
  }
})
class InnerLoaderComponent extends Vue {
  loading: boolean

  render (h: CreateElement) {
    return this.loading ? <Loader /> : <div class='dummy-loader' />
  }
}

@Component({
  computed: {
    displayErrorMessage () {
      return this.$store.state.infiniteScrollState.displayErrorMessage
    },
    loading () {
      return this.$store.state.infiniteScrollState.loading
    }
  }
})
class InnerErrorMessageComponent extends Vue {

  @Prop({ required: false, default: null })
  errorMessage: HTMLElement | null

  displayErrorMessage: boolean
  loading: boolean

  render (h: CreateElement) {
    return (this.displayErrorMessage && !this.loading) ? this.errorMessage : null
  }
}

@Component({
  computed: {
    endOfPageReached () {
      return this.$store.state.infiniteScrollState.endOfPageReached
    },
    loading () {
      return this.$store.state.infiniteScrollState.loading
    },
    displayErrorMessage () {
      return this.$store.state.infiniteScrollState.displayErrorMessage
    }
  }
})
class InnerEndOfPageMessage extends Vue {
  endOfPageReached: boolean
  loading: boolean
  displayErrorMessage: boolean

  render (h: CreateElement) {
    return (!this.loading && !this.displayErrorMessage && this.endOfPageReached)
      ? <div class='end-page-message'>{this.$root.$t('END_OF_PAGE_REACHED')}</div>
      : null
  }
}
@Component({
  computed: {
    loading: {
      get () {
        return this.$store.state.infiniteScrollState.loading
      },
      set (newValue) {
        this.$store.state.infiniteScrollState.loading = newValue
      }
    },
    displayErrorMessage: {
      get () {
        return this.$store.state.infiniteScrollState.displayErrorMessage
      },
      set (newValue) {
        this.$store.state.infiniteScrollState.displayErrorMessage = newValue
      }
    },
    endOfPageReached: {
      get () {
        return this.$store.state.infiniteScrollState.endOfPageReached
      },
      set (newValue) {
        this.$store.state.infiniteScrollState.endOfPageReached = newValue
      }
    }
  }
})
export class InfiniteScrollList extends Vue {

  // Method must return a promise containing the number of available pages.
  @Prop({ required: true })
  fetchNextPageItems: (page: number, itemsPerPage: number) => Promise<{ totalNoOfPages: number }>

  // HTML elements must have a unique key!
  @Prop({ required: true })
  mapItem: (item: any, h: CreateElement) => HTMLElement

  // If an empty items array will be provided, this will reset the current page to 1. This is helpful for when we
  @Prop({ required: true })
  items: any[]

  @Prop({ required: false, default: null })
  errorMessage: HTMLElement | null

  @Prop({ required: false, default: 10 })
  itemsPerPage: number

  // If you use filters on your page, provide them to this component.
  // Every time the filters change, this component wil reset to page 1 and load first page.
  @Prop({ required: false, default: null })
  filters: any

  protected currentPage: number

  protected totalNoOfPages: number

  protected scrollableList: HTMLElement | null

  protected loading: boolean

  protected displayErrorMessage: boolean

  protected endOfPageReached: boolean

  protected scrollToTop

  @Watch('items')
  itemsUpdated () {
    this.loading = false
    this.displayErrorMessage = this.items.length === 0
    if (this.items.length && this.scrollToTop) {
      // Scroll to top as late as possible to avoid flickering.
      this.scrollableList!.scrollTop = 0
      this.scrollToTop = false
    }
  }

  @Watch('filters')
  resetPage () {
    this.scrollToTop = true
    this.currentPage = 1
    this.loadAndAdvance()
  }

  mounted () {
    this.scrollableList = document.getElementById('infinite-scroll-list')
    if (this.scrollableList) {
      this.scrollableList.addEventListener('scroll', this.loadPageAtBottom)
    }
    this.currentPage = 1
    this.loadAndAdvance()
  }

  loadPageAtBottom () {
    if (this.loading || (this.currentPage > this.totalNoOfPages) || !this.scrollableList) return
    let bottomOfWindow = this.scrollableList.scrollTop + this.scrollableList.offsetHeight + BOTTOM_SCROLL_THRESHOLD >= this.scrollableList.scrollHeight

    if (bottomOfWindow) {
      this.loadAndAdvance()
    }
  }

  loadAndAdvance () {
    this.loading = true
    this.fetchNextPageItems(this.currentPage, this.itemsPerPage)
      .then((response) => {
        this.totalNoOfPages = response.totalNoOfPages
        this.endOfPageReached = this.currentPage + 1 >= this.totalNoOfPages
        this.displayErrorMessage = this.items.length === 0
        this.loading = false
      })
      .catch(error => console.log('Failed to load next page', error))
    this.currentPage++
  }

  render (h: CreateElement) {
    return <div class='infinite-scroll-list-wrapper' id='infinite-scroll-list'>
      <transition-group name='fade' appear>
        { this.items.map(item => this.mapItem(item, h)) }
      </transition-group>
      <InnerErrorMessageComponent errorMessage={this.errorMessage} />
      <InnerEndOfPageMessage />
      <InnerLoaderComponent />
    </div>
  }

  protected beforeDestroy () {
    if (this.scrollableList) {
      this.scrollableList.removeEventListener('scroll', this.loadPageAtBottom)
    }
  }
}
